import { LuIcon, Text } from "@overmap-ai/blocks"
import { EMPTY_ARRAY, type User } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { AssigneeMultiSelect } from "../../../components/DropdownMultiSelects/AssigneeMultiSelect"
import { AssigneeSelect } from "../../../components/DropdownSelects/AssigneeSelect"
import { UserIcon } from "../../../components/Icons/UserIcon"
import { useAssignees } from "../../../hooks/users"
import { modelIconMapping } from "../../../mappings/icons"
import { getUserDisplayLabel } from "../../../utils/users"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { AssigneeFilterDef } from "./AssigneeFilterDef"

type AssigneeFilterCellProps = FilterDefRenderProps<AssigneeFilterDef>

export const AssigneeFilterCell = (props: AssigneeFilterCellProps) => {
	const { filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const assignees = useAssignees(filterDef.assignees)
	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedAssignee = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return assignees.find((userOrNull) => (userOrNull?.id ?? null) === filterValue)
	}, [assignees, filterValue, filterDef.modifiers.equals])

	const selectedAssignees = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined
		const set = new Set(filterValue)
		return assignees.filter((userOrNull) => set.has(userOrNull?.id ?? null))
	}, [assignees, filterValue, filterDef.modifiers.includes])

	const handleValueSelect = useCallback(
		(user: User | null | undefined) => {
			filter.setFilterValue(user ? user.id : user)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(users: (User | null)[]) => {
			if (users.length > 0) {
				filter.setFilterValue(users.map((user) => (user ? user.id : null)))
			} else {
				filter.setFilterValue(undefined)
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<AssigneeSelect
						required={false}
						value={selectedAssignee}
						users={assignees}
						onValueChange={handleValueSelect}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow">
							{equalsFilterValue !== undefined ? (
								equalsFilterValue ? (
									<>
										<UserIcon userId={equalsFilterValue} size="xs" />
										<Text className="truncate">{getUserDisplayLabel(selectedAssignee)}</Text>
									</>
								) : (
									<>
										<LuIcon icon={modelIconMapping.unassigned} />
										<span className="truncate">Unassigned</span>
									</>
								)
							) : (
								"..."
							)}
						</FilterCellButton>
					</AssigneeSelect>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedAssignees ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1: {
							const assignee = selectedAssignees![0]

							return assignee ? (
								<>
									<UserIcon userId={assignee.id} size="xs" />
									<Text className="truncate">{getUserDisplayLabel(assignee)}</Text>
								</>
							) : (
								<>
									<LuIcon icon={modelIconMapping.unassigned} />
									<span className="truncate">Unassigned</span>
								</>
							)
						}
						default:
							return (
								<span className="flex items-center gap-1 truncate">
									{selectedAssignees!
										.slice(0, 3)
										.map((assignee, index) =>
											assignee ? (
												<UserIcon key={assignee.id} userId={assignee.id} size="xs" />
											) : (
												<LuIcon
													key={`unassigned-${index}`}
													icon={modelIconMapping.unassigned}
												/>
											),
										)}
									<Text className="truncate">{`${selectedAssignees!.length} assignees`}</Text>
								</span>
							)
					}
				}

				return (
					<AssigneeMultiSelect
						values={selectedAssignees}
						onValuesChange={handleValuesChange}
						users={assignees}
						size={size}
					>
						<FilterCellButton className="shrink-1 min-w-0 grow truncate">{getLabelUi()}</FilterCellButton>
					</AssigneeMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.assignee} />
				Assignee
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
